import { MODELS_PREFIX, withAllowedModelsHint } from "../models";
import { openaiUsage } from "../openai/usage";
import { headerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { domainToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

const modelsEndpoint = ({ baseUrl }: Record<string, string>) =>
  `${(baseUrl ?? "").replace(/\/$/, "")}/models`;

export const azureOpenai = defineConnector({
  identifier: "azureOpenai",
  name: "Azure OpenAI",
  tagLine: "OpenAI models on Azure",
  logo,
  protocol: "http",
  tags: ["ai", "engineering"],
  defaultUrlPattern: ({ baseUrl }) => domainToRegex(baseUrl),
  auth: (app) =>
    headerAuth([
      {
        name: "api-key",
        value: app.secret.apiKey,
      },
    ]),
  steps: [
    {
      title: "Create an Azure OpenAI resource",
      items: [
        {
          kind: "markdown",
          content:
            "In the [Azure portal](https://portal.azure.com), open your Azure OpenAI resource and copy its endpoint and one of its API keys from **Resource Management → Keys and Endpoint**. Then deploy the models you want to use in [Azure AI Foundry](https://ai.azure.com) under **Deployments**.",
        },
      ],
    },
    {
      title: "Enter your Azure OpenAI details",
      items: [
        {
          kind: "url",
          label: "Base URL",
          placeholder: "https://your-resource.openai.azure.com/openai/v1",
          required: true,
          target: { kind: "configuration", field: "baseUrl" },
          helperText:
            "Your resource endpoint including the /openai/v1 path, e.g. https://your-resource.openai.azure.com/openai/v1.",
        },
        {
          kind: "input",
          label: "API key",
          placeholder: "…",
          required: true,
          target: { kind: "secrets", field: "apiKey" },
        },
      ],
    },
    {
      title: "Add your model deployments",
      items: [
        {
          kind: "list",
          label: "Available model deployments",
          prefix: MODELS_PREFIX,
          columns: [{ field: "name", placeholder: "gpt-5" }],
          addLabel: "Add deployment",
          required: true,
          helperText:
            "Enter at least one model deployment name, matching a model you deployed in Azure AI Foundry.",
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: modelsEndpoint,
  },
  publicEnvVars: ({ baseUrl }) => ({
    AZURE_OPENAI_BASE_URL: baseUrl || "",
  }),
  usageHint: (configuration) =>
    withAllowedModelsHint(
      "Use process.env.AZURE_OPENAI_BASE_URL as the Azure OpenAI base URL, which targets the OpenAI-compatible /openai/v1 surface (no api-version needed). Pass a placeholder API key; the proxy injects the real api-key header from the connector.",
      configuration
    ),
  usage: openaiUsage(),
});
